package com.alpha.showcase.common.ui.dialog

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.alpha.showcase.common.networkfile.model.NetworkFile
import com.alpha.showcase.common.networkfile.storage.remote.RcloneRemoteApi
import com.alpha.showcase.common.repo.SourceListRepo
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import org.jetbrains.compose.resources.getString
import showcaseapp.composeapp.generated.resources.Res
import showcaseapp.composeapp.generated.resources.network_error
import showcaseapp.composeapp.generated.resources.network_unknown_error

data class NetworkFolderPickerUiState(
    val currentPath: String = "/",
    val items: List<NetworkFolderItem> = emptyList(),
    val isLoading: Boolean = false,
    val errorMessage: String? = null,
    val displayMode: DisplayMode = DisplayMode.FOLDERS_ONLY
)

data class NetworkFolderItem(
    val name: String,
    val path: String,
    val isDirectory: Boolean,
    val size: Long,
    val lastModified: String
)

enum class DisplayMode {
    FOLDERS_ONLY,
    FOLDERS_AND_FILES
}

class NetworkFolderPickerViewModel : ViewModel() {
    
    private val _uiState = MutableStateFlow(NetworkFolderPickerUiState())
    val uiState: StateFlow<NetworkFolderPickerUiState> = _uiState.asStateFlow()
    
    private var remoteApi: RcloneRemoteApi? = null
    private val sourceListRepo = SourceListRepo()
    
    fun initialize(remote: RcloneRemoteApi, initialPath: String) {
        remoteApi = remote
        _uiState.value = _uiState.value.copy(currentPath = initialPath)
        loadDirectory(initialPath)
    }
    
    fun setDisplayMode(mode: DisplayMode) {
        _uiState.value = _uiState.value.copy(displayMode = mode)
        refreshCurrentPath()
    }
    
    fun navigateToPath(path: String) {
        loadDirectory(path)
    }
    
    fun navigateUp() {
        val currentPath = _uiState.value.currentPath
        if (currentPath.isNotEmpty() && currentPath != "/") {
            val parentPath = if (currentPath.contains("/")) {
                val lastSlashIndex = currentPath.lastIndexOf("/")
                if (lastSlashIndex == 0) "/" else currentPath.substring(0, lastSlashIndex)
            } else {
                "/"
            }
            loadDirectory(parentPath)
        }
    }
    
    fun refreshCurrentPath() {
        loadDirectory(_uiState.value.currentPath)
    }
    
    private fun loadDirectory(path: String) {
        val remote = remoteApi ?: return
        
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(
                isLoading = true,
                errorMessage = null
            )
            
            try {
                val result = getDirectoryItems(remote, path)
                if (result.isSuccess) {
                    val networkFiles = result.getOrDefault(emptyList())
                    val items = networkFiles.map { networkFile ->
                        NetworkFolderItem(
                            name = networkFile.fileName,
                            path = networkFile.path,
                            isDirectory = networkFile.isDirectory,
                            size = networkFile.size,
                            lastModified = networkFile.modTime
                        )
                    }.let { allItems ->
                        // 根据显示模式过滤
                        when (_uiState.value.displayMode) {
                            DisplayMode.FOLDERS_ONLY -> allItems.filter { it.isDirectory }
                            DisplayMode.FOLDERS_AND_FILES -> allItems
                        }
                    }.sortedWith { a, b ->
                        // 文件夹排在前面，然后按名称排序
                        when {
                            a.isDirectory && !b.isDirectory -> -1
                            !a.isDirectory && b.isDirectory -> 1
                            else -> a.name.compareTo(b.name, ignoreCase = true)
                        }
                    }

                    _uiState.value = _uiState.value.copy(
                        currentPath = path,
                        items = items,
                        isLoading = false,
                        errorMessage = null
                    )
                } else {
                    val message = result.exceptionOrNull()?.message
                        ?: getString(Res.string.network_unknown_error)
                    _uiState.value = _uiState.value.copy(
                        isLoading = false,
                        errorMessage = message
                    )
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    errorMessage = e.message ?: getString(Res.string.network_error)
                )
            }
        }
    }
    
    private suspend fun getDirectoryItems(remote: RcloneRemoteApi, path: String): Result<List<NetworkFile>> {
        return try {
            withContext(Dispatchers.Default) {
                sourceListRepo.getSourceFileDirItems(remote, path).map { items ->
                    items.mapNotNull {
                        it as? NetworkFile
                    }
                }
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }
}
